import { FeatureFlags } from '@dupe/common/constants'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/compat/router'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { LibraryCategoryFilter } from '~/src/components/library/libraryModel'
import {
  type UnifiedRecentItem,
  UnifiedRecentItemType,
} from '~/src/hooks/useFetchRecents'
import useFetchProfileData from '~/src/hooks/useFetchProfileData'
import useFetchRecentsInfinite from '~/src/hooks/useFetchRecentsInfinite'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import useLibraryBottles, {
  LIBRARY_BOTTLES_KEY,
} from '~/src/hooks/useLibraryBottles'

/**
 * The side menu's Recents list query (`MenuRecents`). The header item counts
 * the same list it opens, and the identical key means one shared request.
 * Brain (`ChatV2`) threads are the only kind new chats produce; `Dupe` and
 * `Chat` stay for historical rows and the extension sidepanel.
 */
export const MENU_RECENTS_LIMIT = 20
export const MENU_RECENTS_TYPES = [
  UnifiedRecentItemType.Dupe,
  UnifiedRecentItemType.Chat,
  UnifiedRecentItemType.ChatV2,
]

/** When the shopper last opened the header's Recents dropdown (ms epoch). */
export const RECENTS_SEEN_KEY = 'dupe:recents-nav-seen-at'
/** With no "last opened" yet, searches from the past day count as new. */
const FIRST_VISIT_WINDOW_MS = 24 * 60 * 60 * 1000
/** On a page change, refetch the list if it is older than this. */
const REFRESH_AFTER_MS = 30 * 1000

const readSeenAt = (): number | null => {
  try {
    const value = Number(window.localStorage.getItem(RECENTS_SEEN_KEY))
    return Number.isFinite(value) && value > 0 ? value : null
  } catch {
    return null
  }
}

const recentTime = (recent: UnifiedRecentItem) =>
  new Date(recent.timestamp).getTime()

/** How many recents are newer than `seenAt`. */
export const countNewRecents = (recents: UnifiedRecentItem[], seenAt: number) =>
  recents.filter((recent) => recentTime(recent) > seenAt).length

/** Searching Recents reads them this many at a time... */
export const LIBRARY_SEARCH_PAGE = 50
/**
 * ...up to this many. Nearly every shopper has far fewer; the api reads each
 * chat's messages to title it, so the heaviest pages are megabytes there.
 */
export const LIBRARY_SEARCH_CAP = 200

/** How many saves the menus read: the profile page's own first page. */
export const LIBRARY_SAVES_LIMIT = 50

/**
 * The header's "Library" item: whether it shows, the lists it opens (recent
 * searches, saves, and the bottles they own and want), and the number on its
 * badge. The badge counts only searches made since the shopper last opened
 * the menu (or from the past day, before the first open), so it clears once
 * they've looked; saves carry no date, so they never count. Gated on
 * `header-recents-nav`, and only shown once one of the lists has something.
 * No request is made while the flag is off: the header renders on every page.
 */
export default function useLibraryNav() {
  const { featureOn } = useIsFeatureOn(FeatureFlags.HeaderRecentsNav)
  const enabled = Boolean(featureOn)
  const {
    data: recents = [],
    dataUpdatedAt,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useFetchRecentsInfinite({
    limit: MENU_RECENTS_LIMIT,
    enabled,
    types: MENU_RECENTS_TYPES,
  })
  // A category filter on the Recents tab reads its own, server-filtered list
  // (`/api/recents?category=`), so it pages through the shopper's matching
  // searches instead of filtering the first few batches of all of them.
  const [recentsCategory, setRecentsCategory] =
    useState<LibraryCategoryFilter | null>(null)
  const filtered = useFetchRecentsInfinite({
    limit: MENU_RECENTS_LIMIT,
    enabled: enabled && recentsCategory !== null,
    types: MENU_RECENTS_TYPES,
    category: recentsCategory ?? undefined,
  })
  // Searching Recents reads the shopper's whole list (up to a cap) in big
  // pages, since the api can't search chat titles: it builds them from the
  // messages after the read. Matches show from each page as it arrives.
  const [searchingRecents, setSearchingRecents] = useState(false)
  const searchList = useFetchRecentsInfinite({
    limit: LIBRARY_SEARCH_PAGE,
    enabled: enabled && searchingRecents,
    types: MENU_RECENTS_TYPES,
  })
  const searchCapped = searchList.data.length >= LIBRARY_SEARCH_CAP
  // A page that failed stops the reading: retrying it here would loop.
  const searchFailed = searchList.isError || searchList.isFetchNextPageError
  useEffect(() => {
    if (
      searchingRecents &&
      searchList.hasNextPage &&
      !searchList.isFetching &&
      !searchCapped &&
      !searchFailed
    ) {
      searchList.fetchNextPage()
    }
  }, [
    searchingRecents,
    searchList.hasNextPage,
    searchList.isFetching,
    searchCapped,
    searchFailed,
    searchList.fetchNextPage,
  ])
  const searchRecents = useMemo(
    () => searchList.data.slice(0, LIBRARY_SEARCH_CAP),
    [searchList.data]
  )
  const { data: profile } = useFetchProfileData({
    // The profile read also returns finds, which the Library doesn't show.
    limit: 1,
    bookmarksLimit: LIBRARY_SAVES_LIMIT,
    enabled,
  })
  const saves = profile?.bookmarks ?? []
  const { loved, owned, wanted } = useLibraryBottles(enabled)

  // Read after mount: localStorage isn't there during SSR.
  const [seenAt, setSeenAt] = useState<number | null>(null)
  const [firstVisitSince, setFirstVisitSince] = useState<number>(Infinity)
  useEffect(() => {
    setSeenAt(readSeenAt())
    setFirstVisitSince(Date.now() - FIRST_VISIT_WINDOW_MS)
  }, [])
  const newCount = countNewRecents(recents, seenAt ?? firstVisitSince)

  const markSeen = useCallback(() => {
    // The newest recent's own time guards against a client clock behind the
    // server's, which would keep that recent counted as new.
    const newest = recents.reduce(
      (max, recent) => Math.max(max, recentTime(recent) || 0),
      0
    )
    const now = Math.max(Date.now(), newest)
    setSeenAt(now)
    try {
      window.localStorage.setItem(RECENTS_SEEN_KEY, String(now))
    } catch {
      // Private mode / blocked storage: the badge clears for this page only.
    }
  }, [recents])

  // The list stays fresh for five minutes, so a search made since would not
  // show on the badge. On each in-app page change, refetch it once it is more
  // than 30 seconds old (skipping the first render, which just fetched).
  const queryClient = useQueryClient()
  // `asPath` under pages/; under the App Router the compat router is null,
  // so the pathname marks the page change instead.
  const pathname = usePathname()
  const asPath = useRouter()?.asPath ?? pathname
  const isFirstPath = useRef(true)
  useEffect(() => {
    if (isFirstPath.current) {
      isFirstPath.current = false
      return
    }
    if (enabled && Date.now() - dataUpdatedAt > REFRESH_AFTER_MS) {
      // Not the search read: re-reading its pages is heavy, and the panel
      // puts the fresh menu list in front of it.
      queryClient.invalidateQueries({
        queryKey: ['recents-infinite'],
        predicate: (q) => q.queryKey[2] !== LIBRARY_SEARCH_PAGE,
      })
    }
    // Only page changes should trigger this, not data or flag changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath])

  // Opening the menu re-reads saves and bottles: they change on other pages
  // (a save on a product, "I own it" in the chat) that don't refresh these.
  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['profile-data'] })
    queryClient.invalidateQueries({ queryKey: [LIBRARY_BOTTLES_KEY] })
  }, [queryClient])

  const onOpen = useCallback(() => {
    markSeen()
    refresh()
  }, [markSeen, refresh])

  const hasAny =
    recents.length +
      saves.length +
      loved.length +
      owned.length +
      wanted.length >
    0

  return {
    enabled,
    show: enabled && hasAny,
    count: newCount,
    recents,
    /** More recents than loaded so far: their count reads "20+". */
    recentsMore: Boolean(hasNextPage),
    saves,
    /** As many saves as were asked for: there may be more. */
    savesMore: saves.length >= LIBRARY_SAVES_LIMIT,
    loved,
    owned,
    wanted,
    loadMoreRecents: fetchNextPage,
    loadingMoreRecents: isFetchingNextPage,
    recentsByCategory:
      recentsCategory && filtered.isSuccess
        ? {
            category: recentsCategory,
            recents: filtered.data,
            more: Boolean(filtered.hasNextPage),
            loadMore: filtered.fetchNextPage,
            loading: filtered.isFetchingNextPage,
          }
        : null,
    setRecentsCategory,
    recentsSearch: searchingRecents
      ? {
          recents: searchRecents,
          // Reading on until done; a background refresh of pages already
          // read shows no spinner.
          loading:
            searchList.isLoading ||
            searchList.isFetchingNextPage ||
            (Boolean(searchList.hasNextPage) && !searchCapped && !searchFailed),
          capped: searchCapped && Boolean(searchList.hasNextPage),
          failed: searchFailed,
          retry: () =>
            searchList.isFetchNextPageError
              ? searchList.fetchNextPage()
              : searchList.refetch(),
        }
      : null,
    setSearchingRecents,
    onOpen,
  }
}
